"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CommonQuestions() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "What is included in your standard mowing service?",
    answer: "Our standard service includes precision mowing of all turf areas, string trimming around obstacles, mechanical edging of walkways and driveways, and blowing debris off hard surfaces for a clean finish."
  }, {
    question: "How often should my lawn be mowed?",
    answer: "For most Texas lawns, we recommend a weekly schedule during the peak growing season (March–October) and bi-weekly during the dormant months to maintain optimal grass health and appearance."
  }, {
    question: "Do you offer one-time mowing or just contracts?",
    answer: "We offer both flexible one-time cuts and recurring maintenance plans. Our recurring clients enjoy priority scheduling and consistent pricing throughout the season."
  }, {
    question: "What height do you cut the grass at?",
    answer: "We adjust our blade height based on your grass type—typically 2.5 to 3 inches for St. Augustine and slightly lower for Bermuda—to promote deep root growth and drought resistance."
  }, {
    question: "Do you bag the grass clippings?",
    answer: "We prefer mulching clippings back into the lawn as they provide natural nitrogen. However, if the growth is excessive or if you specifically request it, we can bag and remove clippings for an additional fee."
  }, {
    question: "What happens if it rains on my scheduled day?",
    answer: "If heavy rain prevents us from working, we will reschedule your service for the next available clear day. We avoid mowing saturated soil to prevent turf damage and soil compaction."
  }, {
    question: "Are you insured for residential work?",
    answer: "Yes, Acosta's Landscaping is fully insured. We prioritize the safety of your property and our crew, ensuring peace of mind with every visit to your home."
  }, {
    question: "How do I pay for my landscaping services?",
    answer: "We offer convenient digital invoicing. You can pay securely online via credit card or set up automatic payments for recurring maintenance schedules."
  }];
  return <section data-section-id="189" id="common-questions" className="bg-background text-foreground py-32 font-default">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={leftRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <div className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-8">
                [FAQ.08] · SERVICE SPECS
              </div>

              <h2 className="font-default text-foreground text-5xl md:text-[3rem] leading-[1.05] tracking-tighter font-extrabold mb-8">
                Precision,{" "}
                <span className="italic font-light text-primary">defined.</span>
              </h2>

              <p className="font-default text-muted-foreground text-lg leading-relaxed max-w-md mb-10">
                Everything you need to know about our architectural approach to lawn maintenance. From blade height to scheduling logistics.
              </p>

              <Link to="/GetQuotePage" className="inline-flex items-center gap-2 font-mono text-foreground text-base border-b border-primary pb-1 hover:text-primary transition-colors duration-300">
                <span>Still curious? Contact us</span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="w-full">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group data-[state=open]:bg-accent/40 transition-colors duration-400 ease-out">
                  <AccordionTrigger className="hover:no-underline py-7 px-2 md:px-4 [&>svg]:hidden group/trigger">
                    <div className="flex items-start gap-5 md:gap-8 flex-1 text-left">
                      <span className="font-mono text-xs tracking-[0.2em] text-primary pt-2 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default text-foreground text-xl md:text-[1.25rem] leading-snug font-bold">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-5 shrink-0 text-primary transition-transform duration-400 ease-out group-data-[state=open]:rotate-45 mt-2" strokeWidth={2} />
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 px-2 md:px-4">
                    <div className="flex gap-5 md:gap-8">
                      <span className="font-mono text-xs shrink-0 opacity-0 select-none">
                        00
                      </span>
                      <p className="font-default text-muted-foreground text-base md:text-[1.0625rem] leading-[1.75] max-w-2xl">
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}